package ai.wagemix.web.screens.auth

import ai.wagemix.logic.WMRoutes
import ai.wagemix.logic.auth.WMAuthFieldProblems
import ai.wagemix.logic.auth.WMAuthPresenter
import ai.wagemix.logic.auth.WMAuthPresenter.Action
import ai.wagemix.logic.auth.WMAuthPresenter.Event
import ai.wagemix.logic.auth.WMAuthPresenter.Prompt
import ai.wagemix.logic.auth.WMAuthStage
import ai.wagemix.logic.auth.WMEmailProblem
import ai.wagemix.logic.auth.WMNameProblem
import ai.wagemix.logic.auth.WMPasswordProblem
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMField
import ai.wagemix.web.components.WMFieldRow
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMNotice
import ai.wagemix.web.components.WMNoticeKind
import ai.wagemix.web.components.WMTextField
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.theme.WMBodyTextStyle
import ai.wagemix.web.theme.WMLeadTextStyle
import ai.wagemix.web.theme.WMSectionTitleTextStyle
import ai.wagemix.web.components.WMIconTile
import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMPageTitleTextStyle
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import ai.wagemix.web.screens.WMPromptDialog
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.css.WhiteSpace
import org.jetbrains.compose.web.css.FlexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.px

/** Which auth form the page shows. */
enum class WMAuthForm { Login, Register, ForgotPassword, ResetPassword, VerifyEmail }

/**
 * The auth forms (Penpot `Auth / …`): one screen, one view model, the form chosen by [form]. The
 * split layout (brand panel) belongs to the site's auth `@Layout`.
 */
@Composable
fun <P: Prompt, E: Event<P>, A: Action, PR: WMAuthPresenter<P, E, A>, VM: WMBaseAuthWebViewModel<P, E, A, PR>> WMAuthScreen(
    createViewModel: () -> VM,
    form: WMAuthForm,
    strings: WMWebStrings,
    /** The `oobCode` of a reset/verification link. */
    actionCode: String? = null,
    modifier: Modifier = Modifier
) {
    FSWebScreen(createViewModel = createViewModel, key = form to actionCode) { vm ->
        if (actionCode != null) {
            LaunchedEffect(actionCode) {
                when (form) {
                    WMAuthForm.ResetPassword -> vm.userArrivedWithResetCode(actionCode)
                    WMAuthForm.VerifyEmail -> vm.userArrivedWithVerificationCode(actionCode)
                    else -> Unit
                }
            }
        }
        WMAuthFormLayout(
            form = form,
            strings = strings,
            loadingState = vm.loadingState,
            fieldProblemsState = vm.fieldProblemsState,
            stageState = vm.stageState,
            resetEmailState = vm.resetEmailState,
            actionCode = actionCode,
            modifier = modifier,
            onFieldsEdited = vm::userEditedFields,
            onSignIn = vm::userRequestedSignIn,
            onRegister = vm::userRequestedRegister,
            onSendReset = vm::userRequestedPasswordReset,
            onConfirmReset = vm::userRequestedConfirmPasswordReset,
            onResendVerification = vm::userRequestedResendVerification
        )
        val prompt = vm.promptState.value
        val respond = { i: Int -> vm.userRespondedToPrompt(FSPrompt.Response(prompt, i)) }
        WMPromptDialog(prompt = prompt, unwrap = { (it as? Prompt.ComposedSuperPrompt)?.wrapped }, strings = strings, onResponse = respond) { p ->
            when (p) {
                is Prompt.AuthFailed -> WMDialog(
                    title = strings.auth.failedTitle,
                    description = strings.auth.failed(p.reason),
                    onDismiss = { respond(0) },
                    actions = { WMButton(strings.common.close, modifier = WMDialogButtonStyle.toModifier()) { respond(0) } }
                ) {}
                is Prompt.EmailNotVerified -> WMDialog(
                    title = strings.auth.notVerifiedTitle,
                    description = "${strings.auth.notVerifiedMessage} (${p.email})",
                    onDismiss = { respond(Prompt.EmailNotVerified.Selection.Dismiss.ordinal) },
                    actions = {
                        WMButton(strings.common.close, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.EmailNotVerified.Selection.Dismiss.ordinal) }
                        WMButton(strings.auth.resend, modifier = WMDialogButtonStyle.toModifier()) { respond(Prompt.EmailNotVerified.Selection.Resend.ordinal) }
                    }
                ) {}
                else -> Unit
            }
        }
    }
}

@Composable
fun WMAuthFormLayout(
    form: WMAuthForm,
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    fieldProblemsState: State<WMAuthFieldProblems>,
    stageState: State<WMAuthStage>,
    resetEmailState: State<String?>,
    actionCode: String?,
    modifier: Modifier = Modifier,
    onFieldsEdited: (String?, String?, String?, String?, String?) -> Unit,
    onSignIn: (String, String) -> Unit,
    onRegister: (String, String, String, String, String) -> Unit,
    onSendReset: (String) -> Unit,
    onConfirmReset: (String, String, String) -> Unit,
    onResendVerification: () -> Unit
) {
    val au = strings.auth
    val colors = ColorMode.current.toPalette().fs
    val problems = fieldProblemsState.value
    val stage = stageState.value
    val busy = loadingState.value
    var firstName by remember { mutableStateOf("") }
    var lastName by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var confirm by remember { mutableStateOf("") }

    fun nameText(p: WMNameProblem?, field: String, value: String) = when (p) {
        WMNameProblem.Blank -> au.nameBlank
        WMNameProblem.TooLong -> au.tooLong(field, WMAuthPresenter.MAX_NAME_LENGTH, value.trim().length)
        null -> null
    }
    fun emailText(p: WMEmailProblem?) = when (p) { WMEmailProblem.Blank -> au.emailBlank; WMEmailProblem.Invalid -> au.emailInvalid; null -> null }
    fun passwordText(p: WMPasswordProblem?, field: String = au.password, value: String = password) = when (p) {
        WMPasswordProblem.Blank -> au.passwordBlank
        WMPasswordProblem.TooShort -> au.passwordTooShort
        WMPasswordProblem.TooLong -> au.tooLong(field, WMAuthPresenter.MAX_PASSWORD_LENGTH, value.length)
        WMPasswordProblem.NoDigit -> au.passwordNoDigit
        WMPasswordProblem.NoUppercase -> au.passwordNoUppercase
        WMPasswordProblem.NoLowercase -> au.passwordNoLowercase
        WMPasswordProblem.Mismatch -> au.passwordMismatch
        null -> null
    }

    Column(modifier = Modifier.fillMaxWidth().gap(20.px).then(modifier)) {
        when (form) {
            WMAuthForm.Login -> {
                Heading(au.loginTitle, au.loginSubtitle)
                WMField(au.email, errorText = emailText(problems.email)) {
                    WMTextField(email, onTextChange = { email = it; onFieldsEdited(null, null, it, null, null) }, placeholder = au.email, leadingIcon = WMIconGlyph.Mail, valid = problems.email == null)
                }
                WMField(au.password, errorText = passwordText(problems.password)) {
                    WMTextField(password, onTextChange = { password = it }, placeholder = au.password, password = true, showPasswordLabel = au.showPassword, hidePasswordLabel = au.hidePassword, leadingIcon = WMIconGlyph.Lock, valid = problems.password == null, onCommit = { onSignIn(email, password) })
                }
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    AuthLink(WMRoutes.FORGOT_PASSWORD, au.forgotPassword)
                }
                WMButton(strings.common.signIn, fullWidth = true, enabled = !busy) { onSignIn(email, password) }
                AuthPrompt(au.noAccount, WMRoutes.REGISTER, au.createOne)
            }
            WMAuthForm.Register -> {
                Heading(au.registerTitle, au.registerSubtitle)
                WMFieldRow {
                    WMField(au.firstName, errorText = nameText(problems.firstName, au.firstName, firstName)) {
                        WMTextField(firstName, onTextChange = { firstName = it; onFieldsEdited(it, null, null, null, null) }, valid = problems.firstName == null)
                    }
                    WMField(au.lastName, errorText = nameText(problems.lastName, au.lastName, lastName)) {
                        WMTextField(lastName, onTextChange = { lastName = it; onFieldsEdited(null, it, null, null, null) }, valid = problems.lastName == null)
                    }
                }
                WMField(au.email, errorText = emailText(problems.email)) {
                    WMTextField(email, onTextChange = { email = it; onFieldsEdited(null, null, it, null, null) }, leadingIcon = WMIconGlyph.Mail, valid = problems.email == null)
                }
                WMField(au.password, errorText = passwordText(problems.password), helperText = au.passwordRules) {
                    WMTextField(password, onTextChange = { password = it; onFieldsEdited(null, null, null, it, null) }, password = true, showPasswordLabel = au.showPassword, hidePasswordLabel = au.hidePassword, leadingIcon = WMIconGlyph.Lock, valid = problems.password == null)
                }
                WMField(au.confirmPassword, errorText = passwordText(problems.confirmPassword)) {
                    WMTextField(confirm, onTextChange = { confirm = it; onFieldsEdited(null, null, null, password, it) }, password = true, showPasswordLabel = au.showPassword, hidePasswordLabel = au.hidePassword, leadingIcon = WMIconGlyph.Lock, valid = problems.confirmPassword == null, onCommit = { onRegister(firstName, lastName, email, password, confirm) })
                }
                WMButton(strings.common.register, fullWidth = true, enabled = !busy) { onRegister(firstName, lastName, email, password, confirm) }
                Consent(au)
                AuthPrompt(au.haveAccount, WMRoutes.LOGIN, strings.common.signIn)
            }
            WMAuthForm.ForgotPassword -> {
                if (stage == WMAuthStage.ResetLinkSent) {
                    WMIconTile(WMIconGlyph.Mail)
                    Heading(au.checkEmailTitle, au.resetLinkSentMessage(email))
                    WMButton(au.resendEmail, kind = WMButtonKind.Secondary, fullWidth = true, enabled = !busy) { onSendReset(email) }
                    AuthLink(WMRoutes.LOGIN, au.backToLogin)
                } else {
                    Heading(au.forgotTitle, au.forgotSubtitle)
                    WMField(au.email, errorText = emailText(problems.email)) {
                        WMTextField(email, onTextChange = { email = it }, leadingIcon = WMIconGlyph.Mail, valid = problems.email == null, onCommit = { onSendReset(email) })
                    }
                    WMButton(au.sendLink, fullWidth = true, enabled = !busy) { onSendReset(email) }
                    AuthPrompt(au.rememberedIt, WMRoutes.LOGIN, au.backToLogin)
                }
            }
            WMAuthForm.ResetPassword -> {
                if (stage == WMAuthStage.PasswordChanged) {
                    Heading(au.resetDoneTitle, au.resetDoneMessage)
                    WMButton(strings.common.signIn, fullWidth = true) { kotlinx.browser.window.location.href = WMRoutes.LOGIN }
                } else {
                    Heading(au.resetTitle, resetEmailState.value?.let { "${au.resetSubtitle} $it" } ?: "")
                    WMField(au.newPassword, errorText = passwordText(problems.password, au.newPassword), helperText = au.passwordRules) {
                        WMTextField(password, onTextChange = { password = it; onFieldsEdited(null, null, null, it, null) }, password = true, showPasswordLabel = au.showPassword, hidePasswordLabel = au.hidePassword, leadingIcon = WMIconGlyph.Lock, valid = problems.password == null)
                    }
                    WMField(au.confirmNewPassword, errorText = passwordText(problems.confirmPassword)) {
                        WMTextField(confirm, onTextChange = { confirm = it; onFieldsEdited(null, null, null, password, it) }, password = true, showPasswordLabel = au.showPassword, hidePasswordLabel = au.hidePassword, leadingIcon = WMIconGlyph.Lock, valid = problems.confirmPassword == null)
                    }
                    WMButton(au.setPassword, fullWidth = true, enabled = !busy && actionCode != null) { actionCode?.let { onConfirmReset(it, password, confirm) } }
                }
            }
            WMAuthForm.VerifyEmail -> {
                when (stage) {
                    WMAuthStage.VerificationApplied -> {
                        Heading(au.verifiedTitle, au.verifiedMessage)
                        WMButton(au.continueLabel, fullWidth = true) { kotlinx.browser.window.location.href = WMRoutes.PRICING }
                    }
                    WMAuthStage.VerificationSent -> Heading(au.checkEmailTitle, au.checkEmailMessage)
                    else -> {
                        Heading(au.verifyingTitle, "")
                        if (!busy) {
                            WMNotice(au.notVerifiedTitle, kind = WMNoticeKind.Info, message = au.notVerifiedMessage) {
                                WMButton(au.resend, kind = WMButtonKind.Secondary, onClick = onResendVerification)
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Penpot auth links ("Forgot password?", "Create one"): primary, strong label. */
val WMAuthLinkStyle = CssStyle.base {
    Modifier.color(colorMode.toPalette().fs.primary).textDecorationLine(TextDecorationLine.None)
}

@Composable
private fun AuthLink(path: String, text: String) {
    Link(path, text, modifier = WMAuthLinkStyle.toModifier().then(WMLabelStrongTextStyle.toModifier()))
}

/** "Don't have an account? Create one": a quiet prompt and its link. */
@Composable
private fun AuthPrompt(prompt: String, path: String, link: String) {
    val colors = ColorMode.current.toPalette().fs
    Row(modifier = Modifier.fillMaxWidth().gap(6.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
        SpanText(prompt, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
        AuthLink(path, link)
    }
}

/** "By creating an account you agree to the Terms of Use and Privacy Policy." */
@Composable
private fun Consent(au: WMWebStrings.Auth) {
    val colors = ColorMode.current.toPalette().fs
    Span(WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant).toAttrs()) {
        Text(au.consentPrefix)
        Link("/terms", au.termsOfUse, modifier = WMAuthLinkStyle.toModifier())
        Text(au.consentJoin)
        Link("/privacy-policy", au.privacyPolicy, modifier = WMAuthLinkStyle.toModifier())
        Text(".")
    }
}

@Composable
private fun Heading(title: String, subtitle: String) {
    val colors = ColorMode.current.toPalette().fs
    Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
        SpanText(title, WMPageTitleTextStyle.toModifier())
        if (subtitle.isNotBlank()) {
            SpanText(subtitle, WMBodyTextStyle.toModifier().color(colors.onSurfaceVariant))
        }
    }
}
